import { escapeHTML } from '../../utils/escape.js';
import { formatFechaCorta } from '../../utils/formatters.js';
import { agruparPorCategoria } from '../../shared/faq-search-policy.js';

/**
 * Admin "FAQ" editor (openspec/changes/staff-faq/spec.md B5 and its UI brief).
 * Same layout as `EmpleadosView.js`: the form opens as a card above the list.
 * Pure render; `js/faq.js` owns state and listeners.
 */

const FIELD =
    'w-full bg-base border border-border-subtle rounded-2xl px-4 py-3 text-sm text-text-primary focus:outline-none focus:border-brand aria-[invalid=true]:border-red-500';
const LABEL = 'block text-[10px] font-black uppercase tracking-[0.2em] text-text-muted mb-2';
const BTN_PRIMARY =
    'px-8 py-3 bg-brand text-white rounded-2xl font-black uppercase tracking-widest text-xs shadow-lg active:scale-95 transition-transform disabled:opacity-60 disabled:pointer-events-none';
const BTN_SECONDARY =
    'px-4 py-2 rounded-xl border border-border-subtle text-xs font-black uppercase tracking-widest text-text-muted hover:text-brand transition-colors';

/**
 * @param {string} name
 * @param {{ campo?: string } | undefined} error
 */
const invalid = (name, error) => (error?.campo === name ? 'aria-invalid="true" aria-describedby="faq-form-error"' : '');

/**
 * @param {{ modo: 'crear' | 'editar', id?: string, clave?: number, valores: Record<string, any>, error?: { campo: string, mensaje: string }, guardando?: boolean }} form
 * @param {string[]} categorias
 */
function renderForm(form, categorias) {
    const v = form.valores ?? {};
    const texto = (k) => escapeHTML(v[k] ?? '');
    // Arrays when editing a saved entry; the raw textarea text when re-showing
    // a form that failed validation.
    const lineas = (k) => escapeHTML(Array.isArray(v[k]) ? v[k].join('\n') : (v[k] ?? ''));
    const req = '<span class="text-brand" aria-hidden="true">*</span>';
    return `
        <form data-faq-form data-modo="${form.modo}" data-form-clave="${form.clave ?? ''}" novalidate class="bg-surface border border-border-subtle rounded-2xl p-6 mb-8">
            <h3 class="text-sm font-black uppercase tracking-tight text-text-primary mb-5">${form.modo === 'editar' ? 'Editar pregunta' : 'Nueva pregunta'}</h3>
            ${form.error ? `<p id="faq-form-error" role="alert" class="mb-5 px-3 py-2 rounded-xl bg-red-500/10 border border-red-500/40 text-xs font-bold text-red-600 dark:text-red-400">${escapeHTML(form.error.mensaje)}</p>` : ''}
            <div class="grid grid-cols-1 lg:grid-cols-2 gap-5">
                <div class="lg:col-span-2">
                    <label class="${LABEL}" for="faq-f-pregunta">Pregunta ${req}</label>
                    <input id="faq-f-pregunta" name="pregunta" type="text" value="${texto('pregunta')}" class="${FIELD}" ${invalid('pregunta', form.error)} />
                </div>
                <div class="lg:col-span-2">
                    <label class="${LABEL}" for="faq-f-en-corto">En corto ${req}</label>
                    <textarea id="faq-f-en-corto" name="en_corto" rows="2" class="${FIELD}" ${invalid('en_corto', form.error)}>${texto('en_corto')}</textarea>
                </div>
                <div>
                    <label class="${LABEL}" for="faq-f-categoria">Categoría ${req}</label>
                    <input id="faq-f-categoria" name="categoria" type="text" list="faq-categorias" value="${texto('categoria')}" class="${FIELD}" ${invalid('categoria', form.error)} />
                    <datalist id="faq-categorias">${categorias.map((c) => `<option value="${escapeHTML(c)}"></option>`).join('')}</datalist>
                </div>
                <div>
                    <label class="${LABEL}" for="faq-f-avisa-a">Avisá a</label>
                    <input id="faq-f-avisa-a" name="avisa_a" type="text" value="${texto('avisa_a')}" class="${FIELD}" placeholder="Opcional" />
                </div>
                <div class="lg:col-span-2">
                    <label class="${LABEL}" for="faq-f-ojo">Ojo</label>
                    <input id="faq-f-ojo" name="ojo" type="text" value="${texto('ojo')}" class="${FIELD}" placeholder="Opcional: lo que rompe algo si se olvida" />
                </div>
                <div>
                    <label class="${LABEL}" for="faq-f-pasos">Pasos <span class="normal-case tracking-normal font-bold">(uno por línea)</span></label>
                    <textarea id="faq-f-pasos" name="pasos" rows="5" class="${FIELD}">${lineas('pasos')}</textarea>
                </div>
                <div>
                    <label class="${LABEL}" for="faq-f-otras">Otras formas de preguntarlo <span class="normal-case tracking-normal font-bold">(una por línea)</span></label>
                    <textarea id="faq-f-otras" name="otras_formas" rows="5" class="${FIELD}" placeholder="no pasa la tarjeta">${lineas('otras_formas')}</textarea>
                </div>
            </div>
            <div class="flex gap-3 mt-6">
                <button type="submit" class="${BTN_PRIMARY}" ${form.guardando ? 'disabled' : ''}>${form.guardando ? 'Guardando...' : 'Guardar'}</button>
                <button type="button" data-action="faq-cancelar" class="px-8 py-3 rounded-2xl border border-border-subtle text-xs font-black uppercase tracking-widest text-text-muted">Cancelar</button>
            </div>
        </form>
    `;
}

/**
 * @param {Record<string, any>} e
 * @param {boolean} confirmando
 * @param {boolean} borrando
 */
function renderFila(e, confirmando, borrando) {
    const id = escapeHTML(e.id);
    const acciones = confirmando
        ? `<div class="flex items-center gap-2" role="group" aria-label="Confirmar borrado">
                <span class="text-xs font-bold text-text-primary">¿Borrar esta pregunta? No se puede deshacer.</span>
                <button type="button" data-action="faq-borrar-confirmar" data-id="${id}" ${borrando ? 'disabled' : ''} class="px-4 py-2 rounded-xl bg-red-600 text-white text-xs font-black uppercase tracking-widest disabled:opacity-60">${borrando ? 'Borrando...' : 'Borrar'}</button>
                <button type="button" data-action="faq-borrar-cancelar" class="${BTN_SECONDARY}">No</button>
           </div>`
        : `<div class="flex items-center gap-2 shrink-0">
                <button type="button" data-action="faq-editar" data-id="${id}" class="${BTN_SECONDARY}">Editar</button>
                <button type="button" data-action="${e.visible ? 'faq-ocultar' : 'faq-mostrar'}" data-id="${id}" class="${BTN_SECONDARY}">${e.visible ? 'Ocultar' : 'Mostrar'}</button>
                <button type="button" data-action="faq-borrar" data-id="${id}" class="${BTN_SECONDARY}" aria-label="Borrar ${escapeHTML(e.pregunta)}"><i class="fa-solid fa-trash-can" aria-hidden="true"></i></button>
           </div>`;
    return `
        <div data-faq-admin-fila="${id}" class="flex flex-wrap items-center justify-between gap-4 py-4 border-b border-border-subtle last:border-b-0">
            <div class="flex-1 min-w-[16rem] ${e.visible ? '' : 'opacity-60'}">
                <p class="font-bold text-text-primary">
                    ${escapeHTML(e.pregunta)}
                    ${e.visible ? '' : '<span class="ml-2 align-middle px-2 py-0.5 rounded-full bg-black/5 dark:bg-white/10 text-[10px] font-black uppercase tracking-widest text-text-muted">Oculta</span>'}
                </p>
                <p class="text-xs text-text-muted mt-1 truncate">${escapeHTML(e.en_corto)}</p>
                <p class="text-[11px] text-text-muted mt-1">Actualizado el ${escapeHTML(formatFechaCorta(e.updated_at))}</p>
            </div>
            ${acciones}
        </div>
    `;
}

/**
 * @param {{
 *   uiState: { status: 'loading' | 'error' | 'ready', mensaje?: string },
 *   entradas: Array<Record<string, any>>,
 *   form: null | { modo: 'crear' | 'editar', id?: string, clave?: number, valores: Record<string, any>, error?: { campo: string, mensaje: string }, guardando?: boolean },
 *   confirmBorrarId: string | null,
 *   borrandoId?: string | null,
 *   gruposPendientes: number,
 * }} props
 * @returns {string}
 */
export function FaqAdminView({ uiState, entradas, form, confirmBorrarId, borrandoId = null, gruposPendientes }) {
    const aviso =
        gruposPendientes > 0
            ? `<button type="button" data-action="faq-ir-busquedas"
                    class="mb-6 inline-flex items-center gap-2 px-4 py-2 rounded-xl border border-brand text-brand text-xs font-black uppercase tracking-widest hover:bg-brand hover:text-white transition-colors">
                    <i class="fa-solid fa-magnifying-glass" aria-hidden="true"></i>
                    ${gruposPendientes} ${gruposPendientes === 1 ? 'búsqueda sin revisar' : 'búsquedas sin revisar'}
                    <i class="fa-solid fa-arrow-right" aria-hidden="true"></i>
               </button>`
            : '';

    let cuerpo;
    if (uiState.status === 'loading') {
        cuerpo = `
            <div class="flex flex-col items-center justify-center py-16 text-center" role="status" aria-live="polite">
                <i class="fa-solid fa-circle-notch fa-spin text-2xl text-brand mb-4" aria-hidden="true"></i>
                <p class="text-xs font-black text-text-muted uppercase tracking-widest">Cargando preguntas...</p>
            </div>`;
    } else if (uiState.status === 'error') {
        cuerpo = `
            <div class="flex flex-col items-center justify-center py-16 text-center" role="alert">
                <p class="text-sm font-bold text-text-primary mb-6">${escapeHTML(uiState.mensaje ?? 'No se pudieron cargar las preguntas.')}</p>
                <button type="button" data-action="faq-reintentar" class="px-6 py-3 rounded-2xl bg-brand text-white text-xs font-black uppercase tracking-widest shadow-lg">Reintentar</button>
            </div>`;
    } else if (entradas.length === 0) {
        cuerpo = '<p class="py-12 text-center text-sm text-text-muted">Todavía no hay preguntas. Creá la primera con "Nueva pregunta".</p>';
    } else {
        cuerpo = agruparPorCategoria(entradas)
            .map(
                ({ categoria, entradas: lista }) => `
                    <section class="mb-8">
                        <h3 class="${LABEL}">${escapeHTML(categoria)}</h3>
                        <div class="bg-surface border border-border-subtle rounded-2xl px-5">
                            ${lista.map((e) => renderFila(e, e.id === confirmBorrarId, e.id === borrandoId)).join('')}
                        </div>
                    </section>`,
            )
            .join('');
    }

    const categorias = [...new Set(entradas.map((e) => e.categoria))].sort((a, b) => a.localeCompare(b, 'es'));
    return `
        <section data-faq-admin>
            <header class="flex flex-wrap items-center justify-between gap-4 mb-6">
                <div>
                    <h2 class="text-2xl font-black uppercase tracking-tight text-text-primary">FAQ</h2>
                    <p class="mt-1 text-sm text-text-muted">Lo que el staff ve en la pestaña Preguntas.</p>
                </div>
                ${form ? '' : `<button type="button" data-action="faq-nueva" class="${BTN_PRIMARY}"><i class="fa-solid fa-plus mr-2" aria-hidden="true"></i>Nueva pregunta</button>`}
            </header>
            ${aviso}
            ${form ? renderForm(form, categorias) : ''}
            ${cuerpo}
        </section>
    `;
}
